import React, {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useState
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";

import CriteriaScore from "evaluation/CriteriaScore";
import {
	Filter,
	filterCriteria,
	Filtered,
	toFiltered
} from "evaluation/EvaluationUtils";

import useAlert from "lib/components/alert/useAlert";
import Prompt from "lib/components/prompt/Prompt";

import DatePicker from "utils/elements/DatePicker";
import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import FeedbackIconButton from "utils/FeedbackIconButton";
import { asTitle } from "utils/Utils";

import Box from "@mui/material/Box";
import { EvaluationRight } from "core/blueprint/trajectory/TrajectoryBlueprintService";
import EvaluationFilter from "./EvaluationFilter";
import EvaluationOverview from "./EvaluationOverview";
import {
	Cluster,
	Criteria,
	CriteriaType,
	Evaluation,
	updateTrajectoryEvaluation
} from "./TrajectoryService";
import {
	ACTIONBAR_ACTIONS_SLOT_ID,
	ACTIONBAR_FIELDS_SLOT_ID
} from "./TrajectoryViewActionbar";

export type EvaluationViewProps = {
	trajectoryId: number;
	evaluation: Evaluation;
	filter: string;
	rights: boolean[];
	// The day whose state the overview shows; absent means the current state.
	asOf?: Date;

	onSavedEvaluation(): Promise<void>;
	onEditingChange?: (editing: boolean) => void;
};

export type UpdateEvaluationAction = {
	index: number;
	criterion: Criteria;
};

type UpdateAction = UpdateEvaluationAction | "reset";

export type UpdateFilterAction = {
	field: string;
	value: unknown;
};

// The pinned row is rendered by TrajectoryViewActionbar, a sibling above
// EvaluationView, so its slots may not exist yet when this mounts; the lookup
// repeats whenever editing starts or stops, by which time they do.
function useActionbarSlot(id: string, editing: boolean): HTMLElement | null {
	const [slot, setSlot] = useState<HTMLElement | null>(null);
	useEffect(() => {
		setSlot(document.getElementById(id));
	}, [id, editing]);
	return slot;
}

const EvaluationView: React.FC<EvaluationViewProps> = (
	props: EvaluationViewProps
) => {
	const { t } = useTranslation();

	const [edit, setEdit] = useState(false);

	// A save can be triggered from the pinned actionbar, screens away from the
	// top of the tab, so its result is a snackbar rather than a banner up there.
	const feedback = useAlert();

	// While editing, the save and cancel buttons take the place of the
	// trajectory's own in the pinned row, and the Datum picker the place of
	// the as-of picker, so that they ride along wherever you have scrolled.
	const actionsSlot = useActionbarSlot(ACTIONBAR_ACTIONS_SLOT_ID, edit);
	const fieldsSlot = useActionbarSlot(ACTIONBAR_FIELDS_SLOT_ID, edit);

	// Tells the pinned row to make that room: it hides the trajectory's edit
	// and cancel buttons and the as-of picker and shows the slots. Cleared on
	// unmount so a tab switch mid-edit does not leave the row handed over.
	useEffect(() => {
		props.onEditingChange?.(edit);
		return () => props.onEditingChange?.(false);
	}, [edit]);

	const [filter, updateFilter] = useReducer(reduceFilter, {});
	const [filteredCriteria, setFilteredCriteria] = useState<Filtered[]>([]);

	const [editDate, setEditDate] = useState<Date | null>(new Date());

	const [expanded, setExpanded] = useState({ all: false });

	useEffect(() => {
		if (hasFilter()) {
			setFilteredCriteria(
				filterCriteria(props.evaluation.criteria, props.filter, filter)
			);
		} else {
			setFilteredCriteria(
				props.evaluation.criteria.map((criterion, index) =>
					toFiltered(criterion, index)
				)
			);
		}
	}, [props.evaluation.criteria, props.filter, filter]);

	const labels = useMemo(() => {
		const criterionLabels = new Set<string>();

		props.evaluation.criteria.forEach(criterion => {
			getLabels(criterion).forEach(label => criterionLabels.add(label));
		});

		return [...criterionLabels];
	}, []);

	function getLabels(criterion: Criteria): Set<string> {
		const criterionLabels = new Set<string>(criterion.labels);

		if (criterion.type === CriteriaType.CLUSTER) {
			const cluster = criterion as Cluster;
			cluster.subsections.forEach(subsection => {
				getLabels(subsection).forEach(label =>
					criterionLabels.add(label)
				);
			});
		}

		return criterionLabels;
	}

	function hasFilter(): boolean {
		if (props.filter !== "") {
			return true;
		}

		if (filter?.labels) {
			return true;
		}

		if (filter?.changesFrom || filter?.changesUntil) {
			return true;
		}

		if (filter?.offeredFrom || filter?.offeredUntil) {
			return true;
		}

		if (
			filter?.locationSchool ||
			filter?.locationWork ||
			filter?.locationExternal
		) {
			return true;
		}

		if (filter?.achieved !== undefined) {
			return true;
		}

		if (filter?.scoreMin || filter?.scoreMax) {
			return true;
		}

		if (filter?.evaluated !== undefined) {
			return true;
		}

		return false;
	}

	function reduceFilter(state: Filter, action: UpdateFilterAction): Filter {
		return {
			...state,
			[action.field]: action.value
		};
	}

	const getScoreName = React.useCallback(
		(score: number | null): string => {
			if (score === null) {
				return "";
			}

			const index = score - props.evaluation.minScore;
			const scoreName = props.evaluation.scoreNames[index];
			if (scoreName) {
				return scoreName;
			} else {
				return `${score.toString()}/${props.evaluation.maxScore.toString()}`;
			}
		},
		[
			props.evaluation.minScore,
			props.evaluation.maxScore,
			props.evaluation.scoreNames
		]
	);

	const getScoreInfo = React.useCallback(
		(score: number | null): string => {
			if (score === null) {
				return "";
			}

			const index = score - props.evaluation.minScore;
			return props.evaluation.scoreInfo[index];
		},
		[props.evaluation.minScore, props.evaluation.scoreInfo]
	);

	const getScoreColour = React.useCallback(
		(score: number | null): string => {
			if (score === null) {
				return "";
			}

			const index = score - props.evaluation.minScore;
			return props.evaluation.scoreColours[index];
		},
		[props.evaluation.minScore, props.evaluation.scoreColours]
	);

	const saveEvaluation = useCallback((): Promise<void> => {
		return updateTrajectoryEvaluation(props.trajectoryId, props.evaluation)
			.then(() => {
				setEdit(false);
			})
			.then(() => props.onSavedEvaluation())
			.then(() => {
				feedback.success(
					t("saveDocumentSuccess", {
						document: "evaluation"
					})
				);
			})
			.catch((reason: Error) => {
				if (reason.message === ERR_INTERNET_DISCONNECTED) {
					feedback.error(t("internetDisconnectedError"));
				} else {
					feedback.error(
						t("saveDocumentFailure", {
							document: "evaluation"
						})
					);
				}
			});
	}, [props.trajectoryId, props.onSavedEvaluation, props.evaluation]);

	const closeEvaluation = useCallback((): void => {
		if (window.confirm(t("exitDocumentMessage"))) {
			setEdit(false);
		}
	}, []);

	const handleCollapseAll = useCallback((): void => {
		setExpanded({ all: false });
	}, []);

	const handleExpandAll = useCallback((): void => {
		setExpanded({ all: true });
	}, []);

	return (
		<>
			<Box sx={{ displayPrint: "none" }}>
				{!props.rights[EvaluationRight.READ] && (
					<Alert severity="warning">
						{t("noAccess", { section: "evaluation" })}
					</Alert>
				)}

				<EvaluationOverview
					filtered={filteredCriteria}
					asOf={props.asOf}
					criteria={props.evaluation.criteria}
					scoreNames={props.evaluation.scoreNames}
					minScore={props.evaluation.minScore}
					maxScore={props.evaluation.maxScore}
					scoreResolution={props.evaluation.scoreResolution}
					getScoreName={getScoreName}
					getScoreInfo={getScoreInfo}
					getScoreColour={getScoreColour}
				/>

				<EvaluationFilter
					data={filter}
					minScore={props.evaluation.minScore}
					maxScore={props.evaluation.maxScore}
					labels={labels}
					getScoreName={getScoreName}
					getScoreInfo={getScoreInfo}
					getScoreColour={getScoreColour}
					onUpdate={updateFilter}
				/>

				{filteredCriteria.length === 0 && (
					<Alert severity="info">{t("noFilteredDataFound")}</Alert>
				)}
			</Box>

			{!edit && (
				<Grid sx={{ displayPrint: "none" }} container>
					<Grid item>
						<Grid container columnSpacing={2}>
							<Grid item>
								<Tooltip
									title={
										props.asOf !== undefined
											? t("asOfEditDisabled")
											: ""
									}>
									<span>
										<Button
											variant="outlined"
											disabled={
												!props.rights[
													EvaluationRight.WRITE
												] || props.asOf !== undefined
											}
											onClick={() => setEdit(true)}
											startIcon={<AddIcon />}>
											{asTitle(t("createEvaluation"))}
										</Button>
									</span>
								</Tooltip>
							</Grid>
							<Grid item>
								<Tooltip title={asTitle(t("print"))}>
									<IconButton
										aria-label="print-evaluation"
										onClick={window.print}>
										<PrintIcon />
									</IconButton>
								</Tooltip>
							</Grid>
							<Grid item>
								<Tooltip title={asTitle(t("expandAll"))}>
									<IconButton
										aria-label="expand-all"
										onClick={handleExpandAll}>
										<KeyboardDoubleArrowDownIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title={asTitle(t("collapseAll"))}>
									<IconButton
										aria-label="collapse-all"
										onClick={handleCollapseAll}>
										<KeyboardDoubleArrowUpIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			)}

			{edit &&
				actionsSlot !== null &&
				createPortal(
					<>
						<Tooltip title={asTitle(t("save"))}>
							<span>
								<FeedbackIconButton
									id="save-evaluation-button"
									aria-label="save-evaluation"
									onClick={saveEvaluation}
									size="large">
									<SaveIcon />
								</FeedbackIconButton>
							</span>
						</Tooltip>
						<Tooltip title={asTitle(t("cancel"))}>
							<span>
								<IconButton
									id="close-evaluation-button"
									aria-label="close-evaluation"
									onClick={closeEvaluation}
									size="large">
									<CloseIcon />
								</IconButton>
							</span>
						</Tooltip>
					</>,
					actionsSlot
				)}

			{edit &&
				fieldsSlot !== null &&
				createPortal(
					<DatePicker
						label={asTitle(t("date"))}
						inputProps={{
							variant: "standard",
							// The as-of picker's width, whose spot this takes.
							sx: { width: { xs: 130, sm: 150 } }
						}}
						value={editDate}
						onChange={setEditDate}
					/>,
					fieldsSlot
				)}

			{props.asOf !== undefined && (
				<Alert severity="info" sx={{ displayPrint: "none" }}>
					{t("asOfTableNotice", {
						date: format(props.asOf, "dd/MM/yyyy")
					})}
				</Alert>
			)}

			<Table size="small">
				<TableBody>
					{props.evaluation !== undefined &&
						filteredCriteria.map(element => {
							const criteria =
								props.evaluation.criteria[element.index];
							return (
								<CriteriaScore
									key={element.index}
									index={element.index}
									filtered={element.subElements}
									disabled={
										!edit ||
										!props.rights[EvaluationRight.WRITE]
									}
									minScore={props.evaluation.minScore}
									maxScore={props.evaluation.maxScore}
									editDate={editDate}
									criteria={criteria}
									showFields={
										props.rights[EvaluationRight.READ]
									}
									expanded={expanded}
									getScoreName={getScoreName}
									getScoreInfo={getScoreInfo}
									getScoreColour={getScoreColour}
								/>
							);
						})}
				</TableBody>
			</Table>

			<Snackbar
				open={feedback.alert !== null}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				autoHideDuration={6000}
				onClose={feedback.clear}>
				<Alert
					id="evaluation-save-message"
					severity={feedback.alert?.severity}
					onClose={feedback.clear}>
					{feedback.alert?.message}
				</Alert>
			</Snackbar>

			{edit && <Prompt when={edit} message={t("exitDocumentMessage")} />}
		</>
	);
};

export default EvaluationView;
